import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Private VantStride Admin surface.
 *
 * SECURITY MODEL — the client-side checks in this file are UX only.
 * Actual authorization is enforced server-side: this page calls
 * GET /api/admin/me on mount, and any /api/admin/* endpoint must be
 * protected by `require_admin` on the backend. A user landing here
 * without an admin session is redirected/blocked based on the API's
 * 401/403 response — not based on any list of emails shipped in JS.
 */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const { openAuth } = useAuth();
  const [state, setState] = useState({ status: "checking", admin: null, error: "" });

  // noindex / nofollow — do not let this page appear in search engines
  // or link previews even if the URL leaks.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "VantStride Admin";

    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive, nosnippet";
    document.head.appendChild(meta);

    return () => {
      document.title = prevTitle;
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/me");
        if (cancelled) return;
        setState({ status: "authorized", admin: data.admin, error: "" });
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 401) {
          // Not signed in — send them to the landing page and open the sign-in modal.
          setState({ status: "unauthenticated", admin: null, error: "" });
          openAuth({ tab: "login" });
          navigate("/", { replace: true });
        } else if (code === 403) {
          setState({
            status: "forbidden",
            admin: null,
            error: "You do not have permission to view this page.",
          });
        } else {
          setState({
            status: "error",
            admin: null,
            error: "Could not verify administrator access. Please try again.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.status === "checking" || state.status === "unauthenticated") {
    return (
      <div
        className="min-h-[70vh] grid place-items-center bg-white"
        data-testid="admin-checking"
      >
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Verifying access…
        </div>
      </div>
    );
  }

  if (state.status === "forbidden" || state.status === "error") {
    return (
      <div
        className="min-h-[70vh] grid place-items-center bg-white px-6"
        data-testid="admin-forbidden"
      >
        <div className="max-w-md text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-slate-900 text-white grid place-items-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <h1 className="font-display text-3xl text-[#0A2540] mt-4">Access denied</h1>
          <p className="text-slate-600 mt-3 leading-relaxed">
            {state.error || "You do not have permission to view this page."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="admin-back-home"
            className="mt-6 inline-flex items-center justify-center bg-[#0A2540] text-white rounded-full px-5 py-3 text-sm font-semibold hover:bg-[#051A2E] transition-colors active:scale-95"
          >
            Back to VantStride
          </button>
        </div>
      </div>
    );
  }

  // Authorized
  return (
    <div className="min-h-[70vh] bg-white" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
          <ShieldCheck className="w-3.5 h-3.5" />
          Private Administration
        </div>
        <h1
          className="font-display text-4xl sm:text-5xl text-[#0A2540] mt-3"
          data-testid="admin-title"
        >
          VantStride Admin
        </h1>
        <p className="text-slate-600 mt-3 max-w-2xl leading-relaxed">
          Signed in as{" "}
          <span className="font-medium text-[#0A2540]" data-testid="admin-email">
            {state.admin?.email}
          </span>
          . Private overview of VantStride activity — refreshed each time you open
          this page.
        </p>

        <AdminAnalyticsPanel />
      </div>
    </div>
  );
}


function AdminAnalyticsPanel() {
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/analytics/summary");
        if (!cancelled) setData(data) || setStatus("ready");
      } catch (_) {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "loading") {
    return (
      <div className="mt-10 flex items-center gap-2 text-slate-500 text-sm" data-testid="admin-analytics-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
      </div>
    );
  }
  if (status === "error" || !data) {
    return (
      <div className="mt-10 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-analytics-error">
        Could not load analytics right now.
      </div>
    );
  }

  const totals = data.totals || {};
  const top = data.top || {};
  const users = data.users || { total: 0, new_today: 0 };

  return (
    <div className="mt-10 space-y-8" data-testid="admin-analytics-panel">
      {/* --- Row 1: user counts --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard label="Total users" value={users.total} testid="stat-total-users" />
        <StatCard label="New users today" value={users.new_today} testid="stat-new-users-today" />
      </div>

      {/* --- Career Hubs --- */}
      <SectionCard title="Career Hubs" eyebrow="Views & clicks">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard label="Total hub views" value={totals.hub_view || 0} testid="stat-hub-views" />
          <StatCard label="Total hub clicks" value={totals.hub_click || 0} testid="stat-hub-clicks" />
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <TopList
            title="Most viewed"
            rows={top.hub_views}
            labelKey="hub_slug"
            testid="top-hub-views"
          />
          <TopList
            title="Most clicked"
            rows={top.hub_clicks}
            labelKey="hub_slug"
            testid="top-hub-clicks"
          />
        </div>
      </SectionCard>

      {/* --- Affiliate --- */}
      <SectionCard title="Affiliate Links" eyebrow="Course & partner clicks">
        <StatCard label="Total affiliate clicks" value={totals.affiliate_click || 0} testid="stat-affiliate-clicks" />
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <TopList title="Top partners" rows={top.affiliate_partners} labelKey="partner" testid="top-affiliate-partners" />
          <TopList title="Top courses" rows={top.affiliate_resources} labelKey="resource_name" testid="top-affiliate-resources" />
          <TopList title="Top hubs" rows={top.affiliate_hubs} labelKey="hub_slug" testid="top-affiliate-hubs" />
        </div>
      </SectionCard>

      {/* --- Playbooks --- */}
      <SectionCard title="Playbooks" eyebrow="Interest signals">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard label="Playbook views" value={totals.playbook_view || 0} testid="stat-playbook-views" />
          <StatCard label="Purchase-intent clicks" value={totals.playbook_purchase_intent || 0} testid="stat-playbook-purchase-intent" />
        </div>
        <div className="mt-6">
          <TopList
            title="Top purchase-intent playbooks"
            rows={top.playbook_purchase_intent}
            labelKey="playbook_slug"
            testid="top-playbook-purchase-intent"
          />
        </div>
      </SectionCard>
    </div>
  );
}


function StatCard({ label, value, testid }) {
  const display = typeof value === "number" ? value.toLocaleString() : value || 0;
  return (
    <div
      className="rounded-3xl border border-slate-100 bg-white p-6"
      data-testid={testid}
    >
      <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
        {label}
      </p>
      <p className="font-display text-4xl text-[#0A2540] mt-2">{display}</p>
    </div>
  );
}


function SectionCard({ title, eyebrow, children }) {
  return (
    <section className="rounded-3xl border border-slate-100 bg-slate-50/60 p-6 sm:p-8">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
        {eyebrow}
      </div>
      <h2 className="font-display text-2xl text-[#0A2540] mt-2">{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}


function TopList({ title, rows, labelKey, testid }) {
  const list = Array.isArray(rows) ? rows : [];
  return (
    <div
      className="rounded-2xl border border-slate-100 bg-white p-5"
      data-testid={testid}
    >
      <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
        {title}
      </p>
      {list.length === 0 ? (
        <p className="text-sm text-slate-400 mt-3">No data yet</p>
      ) : (
        <ol className="mt-3 space-y-1.5 text-sm">
          {list.slice(0, 8).map((r, i) => (
            <li
              key={`${r[labelKey]}-${i}`}
              className="flex items-center justify-between gap-3 border-b border-slate-100 last:border-b-0 pb-1.5 last:pb-0"
            >
              <span className="text-[#0A2540] truncate">{r[labelKey] || "—"}</span>
              <span className="font-mono text-xs text-slate-500 tabular-nums">
                {typeof r.count === "number" ? r.count.toLocaleString() : r.count}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
